// MARK://dhk8/pos/lib/clients/invoices
// Typed client for dhk8-erp-invoices (CFDI 4.0 facturas). Mirrors sc-restaurant's
// `/v1/invoices` contract so the swap is mechanical when the real service lands.
//
// STATUS: `dhk8-erp-invoices` service is planned (PORT-FROM-SC-RESTAURANT §3
// cluster `erp` → "Reportes/Facturas" + cluster `fiscal`). The stamping side
// belongs to `dhk8-fiscal-cfdi` (see clients/cfdi.ts).
//
// TODO backend: `dhk8-erp-invoices`. Endpoints needed:
//   GET    /v1/invoices?status=&fromDate=&toDate=&customerId=
//   GET    /v1/invoices/:id
//   POST   /v1/invoices/from-order/:orderId
//   POST   /v1/invoices/global-day/preview   { date }
//   POST   /v1/invoices/global-day            { date }
//   POST   /v1/invoices/:id/cancel            { motivo, replacementUuid? }
// Until then, dev returns deterministic seed invoices when `VITE_BILLING_STUB=1`.
//
// F9.9 (ADR-0008) — ESTADO DE MIGRACION A MUTATIONS:
//   cancelInvoice            → mutation cancelInvoice (DONE; satMotivo=motivo).
//   createInvoiceFromOrder   → PENDIENTE (sin backend): el commit 62e39eb expone
//   issueInvoiceFromOrder    →   solo el CRUD generico (create/issue/send/cancel/void).
//   previewGlobalDay         →   NO existe mutation `from-order` ni `global-day`; el
//   generateGlobalDay        →   InvoiceService NO tiene metodo from-order/global. Estos
//   issueGlobalInvoice       →   flujos componen orden→factura / agregan ventas del dia
//                                + efecto SAT — requieren DISENO fiscal/backend nuevo.
//                                Siguen REST hasta esa decision. NO inventar el flujo.

import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { gqlClient } from '../gql.js'
import { PosInvoicesQuery, PosInvoiceQuery, PosCancelInvoiceMutation } from '../queries/invoices.queries.js'

// 2026-05-26 (Lane #178 svcUrl sweep) — el cliente del POS antes construía
// `${apiOrigin()}/v1/invoices` que NO existe en el ingress de api.nintaigo.com
// (devolvía HTTP 404 nginx y dejaba /facturas vacío en producción). Corrección:
// dhk8-erp-invoices expone `/api/invoices` y se accede vía la ruta del ingress
// `/v1/dhk8-erp-invoices/...`. Mismo patrón ya aplicado en admin-shell
// (commits d53cb417 + a3885b5b).
const BASE = svcUrl('dhk8-erp-invoices', '/api/invoices')

// 2026-05-24: default disabled. Antes era '1' (stub ON) → la página /billing
// del POS siempre mostraba facturas hardcoded del seed() aunque ya hubiera
// backend real. Para activarlo en dev, pasar VITE_BILLING_STUB=1 explícito.
const STUB_ENABLED = ((): boolean => {
  try {
    return (import.meta.env.VITE_BILLING_STUB ?? '0') === '1'
  } catch {
    return false
  }
})()

// ── DTOs (mirror of sc-restaurant InvoiceRow) ──────────────────────────────

export type InvoiceStatus = 'DRAFT' | 'PENDING_STAMPING' | 'STAMPED' | 'CANCELLED' | 'STAMPING_FAILED'
export type InvoiceType = 'INGRESO' | 'EGRESO' | 'PAGO' | 'NOMINA'
export type CancellationMotivo = '01' | '02' | '03' | '04'

export interface InvoiceItemDto {
  id: string
  description: string
  satProductCode: string
  satUnitCode: string
  quantity: number
  unitPriceCents: number
  discountCents: number
  taxCents: number
  totalCents: number
  taxRate: number
}

export interface InvoiceCustomerSummary {
  id: string
  rfc: string
  legalName: string
  tradeName: string | null
}

export interface InvoiceDto {
  id: string
  tenantId: string
  branchId: string
  customerId: string | null
  orderId: string | null
  serie: string
  folio: string
  type: InvoiceType
  status: InvoiceStatus
  isGlobalDay: boolean
  paymentMethod: string // PUE | PPD
  paymentForm: string // 01, 03, ...
  cfdiUsage: string
  currency: string
  subtotalCents: number
  discountCents: number
  taxCents: number
  totalCents: number
  cfdiUuid: string | null
  cfdiStampedAt: string | null
  cfdiCancelledAt: string | null
  cfdiCancellationMotivo: string | null
  issuedAt: string
  notes: string | null
  customer: InvoiceCustomerSummary | null
  items: InvoiceItemDto[]
}

export interface InvoiceFilters {
  status?: InvoiceStatus
  fromDate?: string // YYYY-MM-DD
  toDate?: string // YYYY-MM-DD
  customerId?: string
  isGlobalDay?: boolean
}

export interface GlobalDayPreviewDto {
  date: string
  branchId: string
  ordersCount: number
  subtotalCents: number
  discountCents: number
  taxCents: number
  totalCents: number
  orderIds: string[]
}

// ── Stub ───────────────────────────────────────────────────────────────────

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setHours(11, 22, 0, 0)
  d.setDate(d.getDate() - days)
  return d.toISOString()
}

function mkItems(rows: [string, number, number][]): InvoiceItemDto[] {
  return rows.map(([desc, qty, unit], i) => {
    const sub = qty * unit
    const tax = Math.round(sub * 0.16)
    return {
      id: `iit_${i + 1}`,
      description: desc,
      satProductCode: '90101500',
      satUnitCode: 'E48',
      quantity: qty,
      unitPriceCents: unit,
      discountCents: 0,
      taxCents: tax,
      totalCents: sub + tax,
      taxRate: 0.16,
    }
  })
}

function buildInvoice(
  partial: Partial<InvoiceDto> & { id: string; serie: string; folio: string; items: InvoiceItemDto[] },
): InvoiceDto {
  const subtotalCents = partial.items.reduce((s, it) => s + it.unitPriceCents * it.quantity, 0)
  const discountCents = partial.items.reduce((s, it) => s + it.discountCents, 0)
  const taxCents = partial.items.reduce((s, it) => s + it.taxCents, 0)
  const totalCents = subtotalCents - discountCents + taxCents
  return {
    tenantId: 't0',
    branchId: 'b1',
    customerId: null,
    orderId: null,
    type: 'INGRESO',
    status: 'STAMPED',
    isGlobalDay: false,
    paymentMethod: 'PUE',
    paymentForm: '01',
    cfdiUsage: 'S01',
    currency: 'MXN',
    cfdiUuid: null,
    cfdiStampedAt: null,
    cfdiCancelledAt: null,
    cfdiCancellationMotivo: null,
    issuedAt: isoDaysAgo(0),
    notes: null,
    customer: null,
    subtotalCents,
    discountCents,
    taxCents,
    totalCents,
    ...partial,
  }
}

let stub: InvoiceDto[] | null = null

function seed(): InvoiceDto[] {
  return [
    buildInvoice({
      id: 'inv_001',
      serie: 'A',
      folio: '1042',
      orderId: 'ord_101',
      customerId: 'cus_002',
      customer: { id: 'cus_002', rfc: 'GODE561231GR8', legalName: 'Gustavo Durán Buenrostro', tradeName: null },
      cfdiUsage: 'G03',
      paymentForm: '03',
      cfdiUuid: 'A1B2C3D4-1111-2222-3333-444455556666',
      cfdiStampedAt: isoDaysAgo(0),
      issuedAt: isoDaysAgo(0),
      items: mkItems([
        ['Comida corrida', 2, 12000],
        ['Refresco', 2, 3500],
      ]),
    }),
    buildInvoice({
      id: 'inv_002',
      serie: 'A',
      folio: '1041',
      orderId: 'ord_098',
      customerId: 'cus_003',
      customer: {
        id: 'cus_003',
        rfc: 'SCM150525XY1',
        legalName: 'Servicios Creativos México SA de CV',
        tradeName: 'Servicios Creativos',
      },
      cfdiUsage: 'G03',
      paymentForm: '03',
      status: 'PENDING_STAMPING',
      cfdiUuid: null,
      issuedAt: isoDaysAgo(1),
      items: mkItems([
        ['Banquete corporativo (15 pax)', 1, 450000],
        ['Servicio de meseros', 3, 80000],
      ]),
    }),
    buildInvoice({
      id: 'inv_003',
      serie: 'G',
      folio: '0007',
      isGlobalDay: true,
      cfdiUsage: 'S01',
      paymentForm: '01',
      cfdiUuid: 'GGGGEEEE-7777-8888-9999-000011112222',
      cfdiStampedAt: isoDaysAgo(2),
      issuedAt: isoDaysAgo(2),
      notes: 'Factura global del día (ventas al público en general)',
      items: mkItems([['Venta global del día — operaciones del público en general', 1, 1875000]]),
    }),
    buildInvoice({
      id: 'inv_004',
      serie: 'A',
      folio: '1038',
      orderId: 'ord_088',
      customerId: 'cus_002',
      customer: { id: 'cus_002', rfc: 'GODE561231GR8', legalName: 'Gustavo Durán Buenrostro', tradeName: null },
      cfdiUsage: 'G03',
      paymentForm: '04',
      status: 'CANCELLED',
      cfdiUuid: 'C4N3C3LL-aaaa-bbbb-cccc-ddddeeeeffff',
      cfdiStampedAt: isoDaysAgo(8),
      cfdiCancelledAt: isoDaysAgo(7),
      cfdiCancellationMotivo: '02',
      issuedAt: isoDaysAgo(8),
      items: mkItems([['Desayuno ejecutivo', 4, 9500]]),
    }),
  ]
}

function getStub(): InvoiceDto[] {
  if (!stub) stub = seed()
  return stub
}

function dateOf(iso: string): string {
  return iso.slice(0, 10)
}

// ── API ────────────────────────────────────────────────────────────────────

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// Lane #178 (post-sweep adapter) — el endpoint `dhk8-erp-invoices` devuelve
// rows del schema Prisma `Invoice` (sin `serie`, sin `items`, sin desglose
// IVA, sin paymentMethod/Form/cfdiUsage). El cliente POS espera la shape
// `InvoiceDto` (compatibility con la UI existente). Este adapter mapea cada
// row del backend a `InvoiceDto` con defaults seguros para que la pantalla
// `/billing` no crashee. Cuando Lane #175 (facturas end-to-end) entregue los
// campos reales, los defaults dejan de aplicar.
interface BackendInvoiceRow {
  id: string
  tenantId: string
  customerId: string | null
  folio: string | null
  status: string
  totalCents: number
  paidCents?: number
  currency?: string
  issuedAt?: string | null
  paidAt?: string | null
  cfdiUuid?: string | null
  satMotivo?: string | null
  notes?: string | null
  posOrderId?: string | null
  createdAt?: string
  deletedAt?: string | null
}

function mapStatusFromBackend(s: string): InvoiceStatus {
  switch (s) {
    case 'DRAFT': return 'DRAFT'
    case 'ISSUED':
    case 'SENT':
    case 'PARTIALLY_PAID':
    case 'PAID':
    case 'OVERDUE':
      return 'STAMPED'
    case 'CANCELLED': return 'CANCELLED'
    default: return 'DRAFT'
  }
}

function adaptBackendInvoice(row: BackendInvoiceRow): InvoiceDto {
  const total = Number(row.totalCents ?? 0)
  // Backend no separa subtotal/iva; estimamos 16% inclusivo como default MX.
  const tax = Math.round((total * 16) / 116)
  const subtotal = Math.max(0, total - tax)
  return {
    id: row.id,
    tenantId: row.tenantId,
    branchId: '',
    customerId: row.customerId,
    orderId: row.posOrderId ?? null,
    serie: '',
    folio: row.folio ?? '',
    type: 'INGRESO',
    status: mapStatusFromBackend(row.status),
    isGlobalDay: false,
    paymentMethod: 'PUE',
    paymentForm: '01',
    cfdiUsage: 'G03',
    currency: row.currency ?? 'MXN',
    subtotalCents: subtotal,
    discountCents: 0,
    taxCents: tax,
    totalCents: total,
    cfdiUuid: row.cfdiUuid ?? null,
    cfdiStampedAt: row.cfdiUuid ? (row.issuedAt ?? row.createdAt ?? null) : null,
    cfdiCancelledAt: row.status === 'CANCELLED' ? (row.deletedAt ?? null) : null,
    cfdiCancellationMotivo: row.satMotivo ?? null,
    issuedAt: row.issuedAt ?? row.createdAt ?? new Date().toISOString(),
    notes: row.notes ?? null,
    customer: null,
    items: [],
  }
}

// F4.5: adapta la factura del grafo (erp-invoices + customer federado de erp-customers)
// a InvoiceDto. Reusa adaptBackendInvoice para la base AR (mismos campos que el REST,
// fiscales por default) y enriquece customer (federación) + items (lines del grafo) —
// una mejora sobre el path REST que dejaba customer:null/items:[]. serie/isGlobalDay/
// cfdiUsage NO existen en el modelo erp-invoices (campos optimistas del POS) → default.
interface GraphInvoiceLine { id: string; description: string; qty: number; unitCents: number; totalCents: number; taxRate: number; productId: string | null }
interface GraphInvoice {
  id: string; tenantId: string; customerId: string | null; folio: string | null; status: string
  totalCents: number; paidCents: number | null; currency: string | null; issuedAt: string | null
  paidAt: string | null; cfdiUuid: string | null; satMotivo: string | null; notes: string | null
  posOrderId: string | null; createdAt: string
  customer: { id: string; legalName: string | null; rfc: string | null } | null
  lines?: GraphInvoiceLine[]
}
function adaptGraphInvoice(g: GraphInvoice): InvoiceDto {
  const base = adaptBackendInvoice({
    id: g.id, tenantId: g.tenantId, customerId: g.customerId ?? null, folio: g.folio ?? null,
    status: g.status, totalCents: g.totalCents, paidCents: g.paidCents ?? 0, currency: g.currency ?? 'MXN',
    issuedAt: g.issuedAt ?? null, paidAt: g.paidAt ?? null, cfdiUuid: g.cfdiUuid ?? null,
    satMotivo: g.satMotivo ?? null, notes: g.notes ?? null, posOrderId: g.posOrderId ?? null,
    createdAt: g.createdAt ?? undefined,
  })
  const customer: InvoiceCustomerSummary | null = g.customer
    ? { id: g.customer.id, rfc: g.customer.rfc ?? '', legalName: g.customer.legalName ?? '', tradeName: null }
    : null
  const items: InvoiceItemDto[] = (g.lines ?? []).map((l) => ({
    id: l.id, description: l.description, satProductCode: '', satUnitCode: '',
    quantity: l.qty, unitPriceCents: l.unitCents, discountCents: 0,
    taxCents: Math.round((l.totalCents * l.taxRate) / (100 + l.taxRate)), totalCents: l.totalCents, taxRate: l.taxRate,
  }))
  return { ...base, customer, items }
}

/** El endpoint puede devolver `{ items, total, page, limit }` (paginated) o un
 *  array directo. Normalizamos ambos casos. */
function extractRows(body: unknown): BackendInvoiceRow[] {
  if (Array.isArray(body)) return body as BackendInvoiceRow[]
  if (body !== null && typeof body === 'object' && Array.isArray((body as { items?: unknown }).items)) {
    return (body as { items: BackendInvoiceRow[] }).items
  }
  return []
}

export async function listInvoices(filters: InvoiceFilters = {}, signal?: AbortSignal): Promise<InvoiceDto[]> {
  if (STUB_ENABLED) {
    let rows = getStub().slice()
    if (filters.status) rows = rows.filter((r) => r.status === filters.status)
    if (typeof filters.isGlobalDay === 'boolean') rows = rows.filter((r) => r.isGlobalDay === filters.isGlobalDay)
    if (filters.customerId) rows = rows.filter((r) => r.customerId === filters.customerId)
    if (filters.fromDate) rows = rows.filter((r) => dateOf(r.issuedAt) >= filters.fromDate!)
    if (filters.toDate) rows = rows.filter((r) => dateOf(r.issuedAt) <= filters.toDate!)
    return rows.sort((a, b) => b.issuedAt.localeCompare(a.issuedAt))
  }
  // F4.5 / ADR-0009: lectura por el grafo (erp-invoices, tenant del JWT) — misma query
  // que Admin. El grafo filtra status/customerId; fromDate/toDate se filtran en cliente
  // (el query `invoices` no expone rango de fechas). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { limit: number; status?: InvoiceStatus; customerId?: string } = { limit: 100 }
  if (filters.status) vars.status = filters.status
  if (filters.customerId) vars.customerId = filters.customerId
  const res = await gqlClient.query(PosInvoicesQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  let rows = ((res.data?.invoices?.items ?? []) as unknown as GraphInvoice[]).map(adaptGraphInvoice)
  if (filters.fromDate) rows = rows.filter((r) => dateOf(r.issuedAt) >= filters.fromDate!)
  if (filters.toDate) rows = rows.filter((r) => dateOf(r.issuedAt) <= filters.toDate!)
  return rows.sort((a, b) => b.issuedAt.localeCompare(a.issuedAt))
}

export async function getInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDto | null> {
  if (STUB_ENABLED) return getStub().find((r) => r.id === id) ?? null
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosInvoiceQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const inv = res.data?.invoice
  return inv ? adaptGraphInvoice(inv as unknown as GraphInvoice) : null
}

export interface CreateFromOrderInput {
  orderId: string
  customerId?: string
  cfdiUsage?: string
  paymentForm?: string
  paymentMethod?: 'PUE' | 'PPD'
  notes?: string
}

export async function createInvoiceFromOrder(input: CreateFromOrderInput): Promise<InvoiceDto> {
  if (STUB_ENABLED) {
    const next = String(1043 + getStub().length)
    const inv = buildInvoice({
      id: `inv_${Math.random().toString(36).slice(2, 8)}`,
      serie: 'A',
      folio: next,
      orderId: input.orderId,
      customerId: input.customerId ?? null,
      cfdiUsage: input.cfdiUsage ?? 'S01',
      paymentForm: input.paymentForm ?? '01',
      paymentMethod: input.paymentMethod ?? 'PUE',
      status: 'PENDING_STAMPING',
      notes: input.notes ?? null,
      items: mkItems([['Consumo en restaurante', 1, 25000]]),
    })
    stub = [inv, ...getStub()]
    return inv
  }
  return fetchData<InvoiceDto>(`${BASE}/from-order/${input.orderId}`, {
    method: 'POST',
    body: {
      customerId: input.customerId,
      cfdiUsage: input.cfdiUsage,
      paymentForm: input.paymentForm,
      paymentMethod: input.paymentMethod,
      notes: input.notes,
    },
  })
}

// ── facturar-desde-orden / ticket (CFDI 4.0) ───────────────────────────────
// Emisión "completa" en un paso: arma + timbra. La cadena real es
// DocketHub → Omnifiscal → FiscalAPI → SAT; aquí está todo simulado.

export interface FiscalReceiverData {
  rfc: string
  legalName: string
  /** c_UsoCFDI, e.g. 'G03'. */
  usoCfdi: string
  /** c_RegimenFiscal del receptor, e.g. '612'. */
  fiscalRegimeCode: string
  zipCode: string
  email?: string
  /** c_FormaPago, default '01'. */
  paymentForm?: string
  /** Total bruto del ticket en centavos (para cuadrar el CFDI con la orden). */
  totalCents?: number
  /** Descripción de la línea (concepto), default "Consumo en restaurante". */
  concept?: string
}

/** Genera un pseudo folio fiscal (UUID v4-ish) determinista-ish para el stub. */
function fakeUuid(): string {
  const h = () => Math.random().toString(16).slice(2, 6).padStart(4, '0').toUpperCase()
  return `${h()}${h()}-${h()}-4${h().slice(1)}-${(8 + Math.floor(Math.random() * 4)).toString(16).toUpperCase()}${h().slice(1)}-${h()}${h()}${h()}`
}

function itemsForTotal(totalCents: number, concept: string): InvoiceItemDto[] {
  // total = base + IVA(16%)  →  base = round(total / 1.16)
  const base = Math.max(1, Math.round(totalCents / 1.16))
  const tax = totalCents - base
  return [
    {
      id: 'iit_1',
      description: concept,
      satProductCode: '90101500',
      satUnitCode: 'E48',
      quantity: 1,
      unitPriceCents: base,
      discountCents: 0,
      taxCents: tax,
      totalCents: totalCents,
      taxRate: 0.16,
    },
  ]
}

/**
 * Emite (arma + timbra) una factura a partir de una orden/ticket pagado.
 * Devuelve el CFDI ya timbrado con UUID. En modo stub se agrega a la lista
 * en memoria para que aparezca en /billing.
 */
export async function issueInvoiceFromOrder(orderId: string, fiscalData: FiscalReceiverData): Promise<InvoiceDto> {
  if (STUB_ENABLED) {
    const total = fiscalData.totalCents && fiscalData.totalCents > 0 ? fiscalData.totalCents : 25000
    const folio = String(1043 + getStub().length)
    const uuid = fakeUuid()
    const inv = buildInvoice({
      id: `inv_${Math.random().toString(36).slice(2, 8)}`,
      serie: 'A',
      folio,
      orderId,
      customerId: null,
      customer: { id: 'cus_adhoc', rfc: fiscalData.rfc.toUpperCase(), legalName: fiscalData.legalName, tradeName: null },
      cfdiUsage: fiscalData.usoCfdi,
      paymentForm: fiscalData.paymentForm ?? '01',
      paymentMethod: 'PUE',
      status: 'STAMPED',
      cfdiUuid: uuid,
      cfdiStampedAt: new Date().toISOString(),
      issuedAt: new Date().toISOString(),
      notes: fiscalData.email ? `Enviada a ${fiscalData.email}` : null,
      items: itemsForTotal(total, fiscalData.concept ?? 'Consumo en restaurante'),
    })
    stub = [inv, ...getStub()]
    return inv
  }
  return fetchData<InvoiceDto>(`${BASE}/from-order/${orderId}`, { method: 'POST', body: { ...fiscalData, stamp: true } })
}

export async function cancelInvoice(
  id: string,
  motivo: CancellationMotivo,
  replacementUuid?: string,
): Promise<InvoiceDto> {
  if (STUB_ENABLED) {
    const list = getStub()
    const idx = list.findIndex((r) => r.id === id)
    if (idx === -1) throw new Error(`Invoice ${id} not found`)
    const updated: InvoiceDto = {
      ...list[idx]!,
      status: 'CANCELLED',
      cfdiCancelledAt: new Date().toISOString(),
      cfdiCancellationMotivo: motivo,
    }
    stub = list.map((r) => (r.id === id ? updated : r))
    return updated
  }
  // F9.9 / ADR-0008: cancelacion de la fila de factura por el grafo (mutation
  // cancelInvoice, tenant del JWT — erp-invoices scopea por tenantId explicito ->
  // IDOR-guard inherente). El motivo del POS (c_MotivoCancelacion) viaja como
  // satMotivo. La cancelacion del CFDI ante el SAT la dispara fiscal-cfdi por
  // eventos (reservado). replacementUuid (sustitucion 01) se propaga si viene.
  const input: { satMotivo: string; replacementUuid?: string } = { satMotivo: motivo }
  if (replacementUuid) input.replacementUuid = replacementUuid
  const res = await gqlClient.mutation(PosCancelInvoiceMutation, { id, input }).toPromise()
  if (res.error) throw res.error
  const inv = res.data?.cancelInvoice
  if (!inv) throw new Error(`Invoice ${id} not found`)
  return adaptGraphInvoice(inv as unknown as GraphInvoice)
}

export async function previewGlobalDay(date: string, signal?: AbortSignal): Promise<GlobalDayPreviewDto> {
  if (STUB_ENABLED) {
    return {
      date,
      branchId: 'b1',
      ordersCount: 23,
      subtotalCents: 1_616_379,
      discountCents: 0,
      taxCents: 258_621,
      totalCents: 1_875_000,
      orderIds: Array.from({ length: 23 }, (_, i) => `ord_g${i + 1}`),
    }
  }
  return fetchData<GlobalDayPreviewDto>(`${BASE}/global-day/preview`, { method: 'POST', body: { date } })
}

export async function generateGlobalDay(date: string): Promise<InvoiceDto> {
  if (STUB_ENABLED) {
    const folio = String(8 + getStub().filter((r) => r.isGlobalDay).length).padStart(4, '0')
    const inv = buildInvoice({
      id: `inv_${Math.random().toString(36).slice(2, 8)}`,
      serie: 'G',
      folio,
      isGlobalDay: true,
      status: 'PENDING_STAMPING',
      cfdiUsage: 'S01',
      paymentForm: '01',
      notes: `Factura global del día ${date}`,
      issuedAt: `${date}T23:59:00.000Z`,
      items: mkItems([['Venta global del día — operaciones del público en general', 1, 1616379]]),
    })
    stub = [inv, ...getStub()]
    return inv
  }
  return fetchData<InvoiceDto>(`${BASE}/global-day`, { method: 'POST', body: { date } })
}

/** Alias used by the P8 billing module — emits (and stamps) the factura global del día. */
export async function issueGlobalInvoice(date: string): Promise<InvoiceDto> {
  return generateGlobalDay(date)
}

export function tenantIdOrThrow(): string {
  const id = getTenantId()
  if (!id) throw new Error('Sesión no inicializada')
  return id
}

// Test helpers.
export function __resetInvoicesStub(): void {
  stub = null
}
